/**
 * Manda al servidor los errores que rompen una pantalla.
 *
 * Por qué existe. Los tres límites de error del frontend sólo hacían console.error, así
 * que un crash vivía y moría en el navegador de esa persona: sin tabla, sin endpoint,
 * sin nada. El 8-sep-2026 el helpdesk estuvo caído ~50 minutos y nos enteramos porque
 * Andy lo vio con sus ojos — el servidor respondía 200 todo el tiempo, feliz. Y al día
 * siguiente, con un error que le saltaba a un agente al abrir una pantalla, la única
 * forma de investigarlo era pedirle una captura.
 *
 * Un sistema que sólo se entera de sus caídas cuando alguien se queja no se entera:
 * se entera tarde y sólo de las que molestan a alguien que además avisa.
 *
 * REGLA DE ORO: este código corre DENTRO de un boundary que ya atrapó un error. Si
 * fallara, empeoraría exactamente el momento que vino a mejorar. Por eso todo está
 * envuelto, usa fetch pelado (sin interceptores, sin refresh de token, sin reintentos)
 * y cualquier problema se descarta en silencio.
 */

// Un crash suele re-renderizar en loop: sin tope, un usuario podría mandar miles.
// Subido de 5 a 15 al sumar los errores de red y los sueltos: ahora una sesión puede
// encontrarse con varios problemas distintos y no queremos que el sexto se pierda.
// La firma sigue siendo el freno real — lo mismo se manda UNA vez.
const MAX_POR_SESION = 15;
let enviados = 0;
const firmasVistas = new Set();

export function reportClientError({ error, componentStack, boundary }) {
  try {
    if (enviados >= MAX_POR_SESION) return;

    const message = String((error && error.message) || error || '').slice(0, 500);
    if (!message) return;

    const route = (typeof window !== 'undefined' && window.location && window.location.pathname) || '';
    // El mismo error en la misma pantalla se manda UNA vez por sesión. React reintenta
    // el render varias veces; sin esto llegarían cinco copias idénticas de lo mismo.
    const firma = `${boundary}|${message}|${route}`;
    if (firmasVistas.has(firma)) return;
    firmasVistas.add(firma);
    enviados += 1;

    let token = null;
    try { token = localStorage.getItem('accessToken'); } catch { /* modo privado */ }

    const payload = {
      message,
      stack: String((error && error.stack) || '').slice(0, 4000),
      component_stack: String(componentStack || '').slice(0, 4000),
      route,
      boundary: String(boundary || '').slice(0, 60),
      bundle: process.env.REACT_APP_GIT_COMMIT || null,
      user_agent: (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent.slice(0, 300) : null,
    };

    // keepalive para que el envío sobreviva si la pestaña se recarga enseguida
    // (el boundary de chunks hace justamente eso).
    fetch('/api/client-errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => { /* si no se puede avisar, no se puede: nunca romper más */ });
  } catch { /* idem */ }
}

/**
 * Lo que NO atrapaba la primera versión.
 *
 * Al instalar esto el 9-sep sólo se reportaban los errores que hacen explotar un
 * componente de React. Una semana después la tabla seguía en CERO… mientras Thiago
 * encontraba a mano, con F12, ocho pantallas rotas. El motivo es simple: casi nada de
 * lo que él encontró rompe la pantalla. Un endpoint que devuelve 500 deja la pantalla
 * entera, con un cartel de "error al cargar datos" y nada más. Un script que falla en
 * la consola tampoco derriba nada.
 *
 * O sea que el sistema medía lo más raro y se perdía lo más común. Un vigilante que
 * mira donde no pasa nada es peor que no tenerlo, porque da una sensación falsa de que
 * está todo bien: "cero errores" no quería decir cero, quería decir que no mirábamos.
 *
 * Ahora se suman las tres fuentes que faltaban.
 */

/** Una llamada al servidor que devolvió 5xx. Es lo que más aparece en la práctica. */
export function reportApiError(error) {
  try {
    const status = error?.response?.status;
    // Sólo errores del SERVIDOR. Un 401/403/404 es parte de la vida normal de la app
    // (sesión vencida, permiso que no está, algo que se borró) y llenaría la tabla de ruido.
    if (!status || status < 500) return;
    // Y de los 5xx, SÓLO el 500. La diferencia no es de grado, es de naturaleza:
    //   · 500 = la aplicación corrió y se rompió. Eso es un bug nuestro y hay que verlo.
    //   · 502/503/504 = la puerta de entrada no pudo alcanzar a la aplicación. Pasa EN
    //     CADA DESPLIEGUE mientras se reemplaza el contenedor, y no hay nada que
    //     arreglar: en un minuto ya no pasa.
    // Medido: de las 245 filas de la primera semana casi todas eran 502 en ráfaga, a la
    // hora exacta de un despliegue, repartidas entre Ariel, Santi y Ezequiel. El único
    // hallazgo real fue un 500 —el tablero en vivo cayéndose por un parpadeo de Redis—
    // y quedaba sepultado. Filtrar por lista de endpoints no alcanzaba: un despliegue
    // rompe TODO lo que esté en vuelo, no sólo los latidos.
    if (status !== 500) return;

    // Tercera categoría, que faltaba: un 502/503/504 que trae un `code` de aplicación.
    // Eso NO es "el servidor falló" — es al revés: llegamos al servidor, el servidor
    // funcionó, y nos está diciendo con todas las letras que falló algo AGUAS ARRIBA.
    // El caso que lo destapó: /cctv/devices/:id/snapshot devuelve
    // `502 { code: 'CAMERA_UNREACHABLE' }` cuando una cámara no contesta en 8 segundos.
    // Cinco cámaras de un cliente que se está yendo dejaron de responder el 16-sep y
    // cada miniatura del tablero generaba un aviso de "🔌 El servidor falló en una
    // pantalla". Andy lo leyó como una caída de la plataforma; la plataforma estaba
    // perfecta, la cámara no.
    //
    // La distinción es exacta: si el servidor alcanzó a devolver un cuerpo con `code`,
    // respondió — no se cayó. Un 502 SIN `code` (Traefik sin backend, la app muerta,
    // un timeout de verdad) sigue reportándose, que es la señal que importa.
    // Se limita a la familia 502/503/504 a propósito: un 500 con `code` sigue siendo
    // un bug nuestro y tiene que avisar.
    const codigoDeApp = error?.response?.data?.code;
    if (codigoDeApp && [502, 503, 504].includes(status)) return;
    const url = String(error?.config?.url || '');
    // Nunca reportar el reporte: un fallo del propio endpoint no puede generar otro envío.
    if (url.includes('/client-errors')) return;
    // Ya no hace falta una lista de endpoints a ignorar: filtrando por 500 el ruido de
    // despliegue desaparece solo, y un latido que devuelve 500 SÍ es un bug que queremos ver.
    const metodo = String(error?.config?.method || 'get').toUpperCase();
    reportClientError({
      error: { message: `HTTP ${status} en ${metodo} ${url.split('?')[0]}`, stack: '' },
      componentStack: '',
      boundary: 'api',
    });
  } catch { /* nunca romper por reportar */ }
}

/**
 * Errores sueltos de JavaScript y promesas sin atrapar. Acá caen, entre otros, los
 * "Loading chunk failed" — que es exactamente lo que le pasó a Thiago en Personal/ABM
 * y que hasta ahora no dejaba ningún rastro del lado nuestro.
 */
export function installGlobalErrorReporting() {
  if (typeof window === 'undefined' || window.__reporteGlobalInstalado) return;
  window.__reporteGlobalInstalado = true;

  window.addEventListener('error', (ev) => {
    try {
      // Los errores de carga de una imagen o un <script> llegan sin `error` y sin
      // mensaje útil; no aportan y serían ruido constante.
      if (!ev || !ev.error) return;
      reportClientError({ error: ev.error, componentStack: '', boundary: 'window' });
    } catch { /* ídem */ }
  });

  window.addEventListener('unhandledrejection', (ev) => {
    try {
      const r = ev && ev.reason;
      if (!r) return;
      // Un fallo de red ya lo reporta el interceptor con su status; no duplicar.
      if (r.isAxiosError || r.response) return;
      reportClientError({
        error: r instanceof Error ? r : { message: String(r), stack: '' },
        componentStack: '', boundary: 'promise',
      });
    } catch { /* ídem */ }
  });
}

export default reportClientError;
